/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * UI Store - Global UI State Management
 * ============================================================================
 *
 * Manages UI state like sidebar visibility for mobile.
 *
 * ============================================================================
 */

import { create } from 'zustand';
import type { BottomNavSection } from '@/components/layouts/mobile-bottom-nav/types';

interface UIState {
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  expandedSectionId: string | null;
  expandBottomNavSection: (id: string) => void;
  collapseBottomNav: () => void;
  toggleBottomNavSection: (id: string) => void;
  bottomNavSections: BottomNavSection[];
  setBottomNavSections: (sections: BottomNavSection[]) => void;
  isMoreOverlayOpen: boolean;
  toggleMoreOverlay: () => void;
  isModuleSwitcherOpen: boolean;
  toggleModuleSwitcher: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  isSidebarOpen: false,
  openSidebar: () => set({ isSidebarOpen: true }),
  closeSidebar: () => set({ isSidebarOpen: false }),
  toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
  expandedSectionId: null,
  expandBottomNavSection: (id) => set({ expandedSectionId: id }),
  collapseBottomNav: () => set({ expandedSectionId: null, isMoreOverlayOpen: false, isModuleSwitcherOpen: false }),
  toggleBottomNavSection: (id) =>
    set((state) => ({
      expandedSectionId: state.expandedSectionId === id ? null : id,
    })),
  bottomNavSections: [],
  setBottomNavSections: (sections) => set({ bottomNavSections: sections }),
  isMoreOverlayOpen: false,
  toggleMoreOverlay: () =>
    set((state) => ({
      isMoreOverlayOpen: !state.isMoreOverlayOpen,
      expandedSectionId: null,
      isModuleSwitcherOpen: false,
    })),
  isModuleSwitcherOpen: false,
  toggleModuleSwitcher: () =>
    set((state) => ({
      isModuleSwitcherOpen: !state.isModuleSwitcherOpen,
      expandedSectionId: null,
      isMoreOverlayOpen: false,
    })),
}));
